/**
 * Saitera tema – pagrindinis JavaScript failas.
 *
 * Duomenys iš PHP pasiekiami per: window.saiteraData
 */

import GLightbox from 'glightbox';
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade, A11y } from 'swiper/modules';

(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    saitera.mobileMenu.init();
    saitera.dropdowns.init();
    saitera.smoothScroll.init();
    saitera.lightbox.init();
    saitera.sliders.init();
  });

  const saitera = {

    // -----------------------------------------------------------------------
    // Mobilus meniu
    // -----------------------------------------------------------------------
    mobileMenu: {
      init() {
        const toggle = document.querySelector('.menu-toggle');
        const nav    = document.querySelector('.main-navigation');
        if (!toggle || !nav) return;

        toggle.addEventListener('click', () => {
          const isOpen = nav.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', String(isOpen));
        });

        document.addEventListener('click', (e) => {
          if (!nav.contains(e.target)) {
            nav.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
          }
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && nav.classList.contains('is-open')) {
            nav.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            toggle.focus();
          }
        });
      },
    },

    // -----------------------------------------------------------------------
    // Dropdown meniu
    // -----------------------------------------------------------------------
    dropdowns: {
      init() {
        document.querySelectorAll('.menu-item-has-children').forEach((item) => {
          const link    = item.querySelector('a');
          const submenu = item.querySelector('.sub-menu');
          if (!link || !submenu) return;

          link.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const isOpen = item.classList.toggle('is-open');
              link.setAttribute('aria-expanded', String(isOpen));
            }
          });

          item.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
              item.classList.remove('is-open');
              link.setAttribute('aria-expanded', 'false');
              link.focus();
            }
          });
        });
      },
    },

    // -----------------------------------------------------------------------
    // Smooth scroll
    // -----------------------------------------------------------------------
    smoothScroll: {
      init() {
        document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
          anchor.addEventListener('click', function (e) {
            const target = document.querySelector(this.getAttribute('href'));
            if (!target) return;
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
          });
        });
      },
    },

    // -----------------------------------------------------------------------
    // GLightbox – nuotraukų padidinimas
    //
    // Automatiškai veikia su:
    //   1. <a href="nuotrauka.jpg" class="glightbox">
    //   2. <a href="nuotrauka.jpg" data-glightbox>
    //   3. WordPress galerijos (.gallery-item a, .wp-block-gallery a)
    //
    // Papildomos opcijos ant elemento:
    //   data-title="Pavadinimas"
    //   data-description="Aprašymas"
    //   data-gallery="galerijos-pavadinimas"  ← grupuoja į vieną galeriją
    // -----------------------------------------------------------------------
    lightbox: {
      instance: null,

      init() {
        // Automatiškai pridėti .glightbox WordPress galerijų nuorodoms
        document
          .querySelectorAll(
            '.gallery-item a[href], .wp-block-gallery a[href], .blocks-gallery-item a[href]'
          )
          .forEach((link) => {
            if (this.isImageUrl(link.href)) {
              link.classList.add('glightbox');
              // Grupuoti galerijos nuotraukas kartu
              if (!link.dataset.gallery) {
                link.dataset.gallery = 'wp-gallery';
              }
            }
          });

        // Inicializuoti GLightbox
        this.instance = GLightbox({
          selector      : '.glightbox',
          touchNavigation: true,
          loop          : true,
          zoomable      : false,
          keyboardNavigation: true,
          closeOnOutsideClick: true,
        });
      },

      isImageUrl(url) {
        return /\.(jpe?g|png|gif|webp|svg|avif)(\?.*)?$/i.test(url);
      },
    },

    // -----------------------------------------------------------------------
    // Swiper – slaidai / karuselės
    //
    // Naudojimas HTML:
    //   <div class="swiper" data-swiper>
    //     <div class="swiper-wrapper">
    //       <div class="swiper-slide">Turinys</div>
    //     </div>
    //     <button class="swiper-button-prev"></button>
    //     <button class="swiper-button-next"></button>
    //     <div class="swiper-pagination"></div>
    //   </div>
    //
    // Su pasirinktinėmis opcijomis (JSON):
    //   <div class="swiper" data-swiper='{"loop":true,"autoplay":{"delay":4000}}'>
    //
    // Visos palaikomos opcijos:
    //   loop           → true / false
    //   autoplay       → {"delay": 4000} arba false
    //   effect         → "slide" | "fade"
    //   slidesPerView  → 1 | 2 | 3 | "auto"
    //   spaceBetween   → 24
    //   centeredSlides → true / false
    //   speed          → 600  (animacijos greitis ms)
    // -----------------------------------------------------------------------
    sliders: {
      instances: [],

      init() {
        document.querySelectorAll('.swiper[data-swiper]').forEach((el) => {
          const userOpts = this.parseOptions(el.dataset.swiper);

          const hasPrev       = el.querySelector('.swiper-button-prev');
          const hasNext       = el.querySelector('.swiper-button-next');
          const hasPagination = el.querySelector('.swiper-pagination');

          // Numatytosios opcijos
          const defaults = {
            modules      : [A11y],
            speed        : 500,
            a11y         : { enabled: true },
            injectStyles : false, // Swiper savo CSS nenaudojame – turime savo
          };

          // Navigacija – tik jei HTML elementai egzistuoja
          if (hasPrev && hasNext) {
            defaults.modules.push(Navigation);
            defaults.navigation = {
              prevEl: hasPrev,
              nextEl: hasNext,
            };
          }

          // Paginacija – tik jei HTML elementas egzistuoja
          if (hasPagination) {
            defaults.modules.push(Pagination);
            defaults.pagination = {
              el       : hasPagination,
              clickable: true,
            };
          }

          // Autoplay modulis
          if (userOpts.autoplay) {
            defaults.modules.push(Autoplay);
          }

          // Fade efektas
          if (userOpts.effect === 'fade') {
            defaults.modules.push(EffectFade);
          }

          const swiper = new Swiper(el, { ...defaults, ...userOpts });
          this.instances.push(swiper);
        });
      },

      parseOptions(raw) {
        if (!raw || raw.trim() === '' || raw.trim() === 'true') return {};
        try {
          return JSON.parse(raw);
        } catch {
          console.warn('[Saitera] Swiper: neteisingas JSON:', raw);
          return {};
        }
      },
    },

    // -----------------------------------------------------------------------
    // AJAX pagalbinė funkcija
    // Naudojimas: saitera.ajax({ action: 'my_action', data: {...} })
    // -----------------------------------------------------------------------
    ajax(params) {
      const formData = new FormData();
      formData.append('action', params.action);
      formData.append('nonce', window.saiteraData?.nonce ?? '');

      if (params.data) {
        Object.entries(params.data).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      return fetch(window.saiteraData?.ajaxUrl ?? '/wp-admin/admin-ajax.php', {
        method     : 'POST',
        body       : formData,
        credentials: 'same-origin',
      }).then((res) => {
        if (!res.ok) throw new Error('Tinklo klaida');
        return res.json();
      });
    },
  };

  window.saitera = saitera;
})();
